import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { 
  PenTool, Plus, Send, Download, Trash2, Clock, CheckCircle, 
  XCircle, Copy, FileText, RefreshCw, ExternalLink, MessageCircle, 
  Mail, Eye, Edit, Save, Code, Variable, FileSignature, Loader2,
  AlertCircle, HelpCircle, User, Briefcase
} from "lucide-react";
import DataStateError from "../components/DataStateError";
import { useConferma } from "../components/ConfirmProvider";
// 29/09/2026 — date gg/mm/aaaa da un'unica fonte (qui si usava date-fns).
import { formatDateTime } from "../lib/format";

export default function SignatureManagementPage() {
  const [signatures, setSignatures] = useState([]);
  const conferma = useConferma();
  const [clients, setClients] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [availableVariables, setAvailableVariables] = useState({});
  const [loading, setLoading] = useState(true);
  // Distingue "non c'e' niente" da "il caricamento e' fallito": senza questo
  // un guasto di rete sembrava una pagina vuota.
  const [loadError, setLoadError] = useState(false);
  const [activeTab, setActiveTab] = useState("requests");
  
  // New request dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedPolicy, setSelectedPolicy] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [documentTitle, setDocumentTitle] = useState("");
  const [documentContent, setDocumentContent] = useState("");
  const [creating, setCreating] = useState(false);
  
  // Preview dialog
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Template editor dialog
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [newTemplateTitle, setNewTemplateTitle] = useState("");
  const [newTemplateContent, setNewTemplateContent] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  // Generated link dialog
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [generatedLink, setGeneratedLink] = useState("");
  const [generatedClientName, setGeneratedClientName] = useState("");
  const [generatedSignatureId, setGeneratedSignatureId] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoadError(false);
    setLoading(true);
    try {
      const [sigRes, clientRes, policyRes, templateRes, varsRes] = await Promise.all([
        axios.get("/api/signature/requests"),
        axios.get("/api/clients"),
        axios.get("/api/policies"),
        axios.get("/api/signature/templates"),
        axios.get("/api/signature/variables")
      ]);
      setSignatures(sigRes.data || []);
      setClients(clientRes.data.items || clientRes.data || []);
      setPolicies(policyRes.data.items || policyRes.data || []);
      setTemplates(templateRes.data || []);
      setAvailableVariables(varsRes.data || {});
    } catch (err) {
      console.error("Errore caricamento firme:", err);
      setLoadError(true);
      toast.error("Errore nel caricamento dati: " + (err.response?.data?.detail || err.message));
      setSignatures([]);
      setClients([]);
      setPolicies([]);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplate = async (templateId) => {
    if (!templateId) return;
    
    try {
      // Find template in loaded templates
      const tpl = templates.find(t => t.template_id === templateId || t.type === templateId);
      if (tpl) {
        setDocumentTitle(tpl.title);
        setDocumentContent(tpl.content);
        setSelectedTemplate(templateId);
      }
    } catch (err) {
      toast.error("Errore caricamento template");
    }
  };

  const generatePreview = async () => {
    if (!documentContent) {
      toast.error("Inserisci il contenuto del documento");
      return;
    }
    
    setPreviewLoading(true);
    try {
      const res = await axios.post("/api/signature/preview", null, {
        params: {
          template_content: documentContent,
          client_id: selectedClient || undefined,
          policy_id: selectedPolicy && selectedPolicy !== "none" ? selectedPolicy : undefined
        }
      });
      setPreviewContent(res.data.preview_text);
      setPreviewOpen(true);
    } catch (err) {
      toast.error("Errore generazione anteprima");
    } finally {
      setPreviewLoading(false);
    }
  };

  const createSignatureRequest = async () => {
    if (!selectedClient || !documentTitle || !documentContent) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post("/api/signature/create-request", {
        client_id: selectedClient,
        policy_id: selectedPolicy && selectedPolicy !== "none" ? selectedPolicy : null,
        document_type: selectedTemplate || "other",
        document_title: documentTitle,
        document_content: documentContent,
        expiry_hours: 72
      });

      const { signature_id, token, client_name } = response.data;
      const fullUrl = `${window.location.origin}/firma/${token}`;
      
      setGeneratedLink(fullUrl);
      setGeneratedClientName(client_name);
      setGeneratedSignatureId(signature_id);
      
      setDialogOpen(false);
      setLinkDialogOpen(true);
      
      // Reset form
      setSelectedClient("");
      setSelectedPolicy("");
      setSelectedTemplate("");
      setDocumentTitle("");
      setDocumentContent("");
      
      fetchData();
      toast.success("Richiesta firma creata!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella creazione");
    } finally {
      setCreating(false);
    }
  };

  const sendViaWhatsApp = async (signatureId) => {
    try {
      const res = await axios.post(`/api/signature/send-whatsapp/${signatureId}`);
      // v3.9.1: il backend prepara il messaggio, WhatsApp si apre gia' compilato
      if (res.data.whatsapp_url) {
        window.open(res.data.whatsapp_url, "_blank");
      }
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio WhatsApp");
    }
  };

  const sendViaEmail = async (signatureId) => {
    try {
      const res = await axios.post("/api/signature/send-email", { 
        signature_id: signatureId,
        frontend_url: window.location.origin
      });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      const errorMsg = err.response?.data?.detail;
      // Handle validation errors (array of objects)
      if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map(e => e.msg || e.message).join(", "));
      } else if (typeof errorMsg === 'object') {
        toast.error(JSON.stringify(errorMsg));
      } else {
        toast.error(errorMsg || "Errore invio email");
      }
    }
  };

  const downloadSignedDocument = async (sig) => {
    if (!sig.document_id) {
      toast.error("Documento firmato non disponibile");
      return;
    }
    
    try {
      const response = await axios.get(`/api/documents/download/${sig.document_id}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${sig.document_title}_firmato.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error("Errore download documento");
    }
  };

  const deleteSignature = async (signatureId) => {
    if (!(await conferma({
      titolo: "Sei sicuro di voler eliminare questa richiesta?",
      descrizione: "La richiesta di firma verrà eliminata definitivamente.",
    }))) return;
    
    try {
      await axios.delete(`/api/signature/${signatureId}`);
      toast.success("Richiesta eliminata");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const saveTemplate = async () => {
    if (!newTemplateTitle || !newTemplateContent) {
      toast.error("Compila titolo e contenuto");
      return;
    }
    
    setSavingTemplate(true);
    try {
      if (editingTemplate && !editingTemplate.is_default) {
        // Update existing
        await axios.put(`/api/signature/templates/${editingTemplate.template_id}`, null, {
          params: { title: newTemplateTitle, content: newTemplateContent }
        });
        toast.success("Template aggiornato");
      } else {
        // Create new
        await axios.post("/api/signature/templates", null, {
          params: { title: newTemplateTitle, content: newTemplateContent }
        });
        toast.success("Template creato");
      }
      
      setTemplateDialogOpen(false);
      setEditingTemplate(null);
      setNewTemplateTitle("");
      setNewTemplateContent("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!(await conferma({
      titolo: "Sei sicuro di voler eliminare questo template?",
      descrizione: "Il template verrà eliminato definitivamente.",
    }))) return;
    
    try {
      await axios.delete(`/api/signature/templates/${templateId}`);
      toast.success("Template eliminato");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione");
    }
  };

  const insertVariable = (varName) => {
    setNewTemplateContent(prev => prev + `{${varName}}`);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "signed":
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Firmato</Badge>;
      case "pending":
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />In attesa</Badge>;
      case "expired":
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Scaduto</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // 29/09/2026 — la data viene dall'helper unico (gg/mm/aaaa HH:MM).
  const formatDate = (dateStr) => formatDateTime(dateStr, "-");

  const pendingCount = signatures.filter(s => s.status === "pending").length;
  const signedCount = signatures.filter(s => s.status === "signed").length;
  const expiredCount = signatures.filter(s => s.status === "expired").length;

  if (loadError) {
    return (
      <div className="space-y-6" data-testid="pagina-errore-caricamento">
        <DataStateError
          titolo="Non riesco a caricare le firme"
          messaggio="I dati non sono andati persi: e' il collegamento che non ha risposto."
          onRetry={fetchData}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="signature-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground tracking-tight flex items-center gap-2">
            <PenTool className="h-8 w-8 text-blue-500" />
            Firma Digitale
          </h1>
          <p className="text-muted-foreground">Gestisci le firme elettroniche dei documenti</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Aggiorna
          </Button>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nuova Richiesta Firma
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Totale Richieste</p>
                <p className="text-2xl font-bold">{signatures.length}</p>
              </div>
              <FileText className="h-8 w-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">In Attesa</p>
                <p className="text-2xl font-bold text-amber-500">{pendingCount}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Firmati</p>
                <p className="text-2xl font-bold text-green-500">{signedCount}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Scaduti</p>
                <p className="text-2xl font-bold text-red-500">{expiredCount}</p>
              </div>
              <XCircle className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="requests" data-testid="tab-requests">
            <FileSignature className="h-4 w-4 mr-2" />
            Richieste Firma
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">
            <FileText className="h-4 w-4 mr-2" />
            Template Documenti
          </TabsTrigger>
        </TabsList>

        {/* Requests Tab */}
        <TabsContent value="requests" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Richieste di Firma</CardTitle>
              <CardDescription>Elenco di tutti i documenti inviati per la firma</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : signatures.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <PenTool className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessuna richiesta di firma</p>
                  <Button className="mt-4" onClick={() => setDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Crea la prima richiesta
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Documento</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead className="hidden md:table-cell">Creato</TableHead>
                      <TableHead className="hidden md:table-cell">Firmato</TableHead>
                      <TableHead className="text-right">Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {signatures.map((sig) => (
                      <TableRow key={sig.signature_id}>
                        <TableCell className="font-medium">{sig.client_name}</TableCell>
                        <TableCell>
                          <div>
                            <p>{sig.document_title}</p>
                            {sig.email_sent && (
                              <Badge variant="outline" className="mt-1 text-xs">
                                <Mail className="h-3 w-3 mr-1" />Email inviata
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(sig.status)}</TableCell>
                        <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                          {formatDate(sig.created_at)}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                          {sig.signed_at ? formatDate(sig.signed_at) : "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {sig.status === "pending" && (
                              <>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          const fullUrl = `${window.location.origin}/firma/${sig.token}`;
                                          navigator.clipboard.writeText(fullUrl);
                                          toast.success("Link copiato!");
                                        }}
                                      >
                                        <Copy className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Copia link</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => sendViaWhatsApp(sig.signature_id)}
                                      >
                                        <MessageCircle className="h-4 w-4 text-green-500" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Invia via WhatsApp</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => sendViaEmail(sig.signature_id)}
                                      >
                                        <Mail className="h-4 w-4 text-blue-500" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Invia via Email</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </>
                            )}
                            {sig.status === "signed" && sig.document_id && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => downloadSignedDocument(sig)}
                                    >
                                      <Download className="h-4 w-4 text-green-500" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Scarica documento firmato</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deleteSignature(sig.signature_id)}
                                  >
                                    <Trash2 className="h-4 w-4 text-red-500" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Elimina</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Template Documenti</h3>
              <p className="text-sm text-muted-foreground">
                Gestisci i template per i documenti da firmare
              </p>
            </div>
            <Button onClick={() => {
              setEditingTemplate(null);
              setNewTemplateTitle("");
              setNewTemplateContent("");
              setTemplateDialogOpen(true);
            }}>
              <Plus className="h-4 w-4 mr-2" />
              Nuovo Template
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((tpl) => (
              <Card key={tpl.template_id} className="relative">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-base">{tpl.title}</CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        {tpl.is_default ? (
                          <Badge variant="secondary">Predefinito</Badge>
                        ) : (
                          <Badge variant="outline">Personalizzato</Badge>
                        )}
                      </div>
                    </div>
                    <FileText className="h-5 w-5 text-muted-foreground" />
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                    {tpl.content?.substring(0, 150)}...
                  </p>
                  {tpl.variables && tpl.variables.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-muted-foreground mb-1">Variabili:</p>
                      <div className="flex flex-wrap gap-1">
                        {tpl.variables.slice(0, 5).map((v) => (
                          <Badge key={v} variant="outline" className="text-xs">
                            {`{${v}}`}
                          </Badge>
                        ))}
                        {tpl.variables.length > 5 && (
                          <Badge variant="outline" className="text-xs">
                            +{tpl.variables.length - 5}
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => {
                        setEditingTemplate(tpl);
                        setNewTemplateTitle(tpl.title);
                        setNewTemplateContent(tpl.content);
                        setTemplateDialogOpen(true);
                      }}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      {tpl.is_default ? "Visualizza" : "Modifica"}
                    </Button>
                    {!tpl.is_default && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteTemplate(tpl.template_id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Variables Reference */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Variable className="h-5 w-5" />
                Variabili Disponibili
              </CardTitle>
              <CardDescription>
                Usa queste variabili nei template per inserire dati dinamici
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {Object.entries(availableVariables).map(([category, vars]) => (
                  <div key={category}>
                    <h4 className="font-medium capitalize mb-2 flex items-center gap-1">
                      {category === "broker" && <Briefcase className="h-4 w-4" />}
                      {category === "client" && <User className="h-4 w-4" />}
                      {category === "policy" && <FileText className="h-4 w-4" />}
                      {category === "other" && <Code className="h-4 w-4" />}
                      {category === "broker" ? "Broker" : 
                       category === "client" ? "Cliente" : 
                       category === "policy" ? "Polizza" : "Altro"}
                    </h4>
                    <div className="space-y-1">
                      {vars.map((v) => (
                        <div key={v.name} className="flex items-center gap-2">
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                            {`{${v.name}}`}
                          </code>
                          <span className="text-xs text-muted-foreground">
                            {v.description}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Request Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuova Richiesta Firma</DialogTitle>
            <DialogDescription>
              Crea un documento da inviare al cliente per la firma digitale
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left column - Form */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <Select value={selectedClient} onValueChange={setSelectedClient}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.filter(c => c.client_id).map((client) => (
                      <SelectItem key={client.client_id} value={client.client_id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Polizza (opzionale)</Label>
                <Select value={selectedPolicy} onValueChange={setSelectedPolicy}>
                  <SelectTrigger>
                    <SelectValue placeholder="Nessuna polizza" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuna</SelectItem>
                    {policies
                      .filter(p => p.client_id === selectedClient && p.policy_id)
                      .map((policy) => (
                        <SelectItem key={policy.policy_id} value={policy.policy_id}>
                          {policy.policy_number} - {policy.policy_type}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Template Documento</Label>
                <Select value={selectedTemplate} onValueChange={loadTemplate}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona template o scrivi manualmente" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.filter(t => t.type || t.template_id).map((t) => (
                      <SelectItem key={t.template_id || t.type} value={t.template_id || t.type}>
                        {t.title} {t.is_default ? "(Predefinito)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Titolo Documento *</Label>
                <Input
                  value={documentTitle}
                  onChange={(e) => setDocumentTitle(e.target.value)}
                  placeholder="Es: Mandato di Brokeraggio"
                />
              </div>
            </div>

            {/* Right column - Content */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Contenuto Documento *</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={generatePreview}
                  disabled={previewLoading || !documentContent}
                >
                  {previewLoading ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Eye className="h-4 w-4 mr-1" />
                  )}
                  Anteprima
                </Button>
              </div>
              <Textarea
                value={documentContent}
                onChange={(e) => setDocumentContent(e.target.value)}
                rows={15}
                className="font-mono text-sm"
                placeholder="Scrivi il contenuto del documento...&#10;&#10;Usa le variabili come {client_name}, {broker_name}, ecc."
              />
              <p className="text-xs text-muted-foreground">
                Usa variabili come {"{client_name}"}, {"{broker_name}"}, {"{date}"} per inserire dati dinamici
              </p>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={createSignatureRequest}
              disabled={creating || !selectedClient || !documentTitle || !documentContent}
            >
              {creating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Crea Richiesta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Anteprima Documento</DialogTitle>
            <DialogDescription>
              Ecco come apparirà il documento al cliente
            </DialogDescription>
          </DialogHeader>
          <div className="border rounded-lg p-6 bg-white dark:bg-gray-900">
            <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed">
              {previewContent}
            </pre>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Editor Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? (editingTemplate.is_default ? "Visualizza Template" : "Modifica Template") : "Nuovo Template"}
            </DialogTitle>
            <DialogDescription>
              {editingTemplate?.is_default 
                ? "Questo è un template predefinito. Puoi copiare il contenuto per creare un nuovo template personalizzato."
                : "Crea o modifica un template per i documenti da firmare"
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Editor */}
            <div className="lg:col-span-2 space-y-4">
              <div className="space-y-2">
                <Label>Titolo Template *</Label>
                <Input
                  value={newTemplateTitle}
                  onChange={(e) => setNewTemplateTitle(e.target.value)}
                  placeholder="Es: Mandato di Brokeraggio Personalizzato"
                  disabled={editingTemplate?.is_default}
                />
              </div>
              <div className="space-y-2">
                <Label>Contenuto *</Label>
                <Textarea
                  value={newTemplateContent}
                  onChange={(e) => setNewTemplateContent(e.target.value)}
                  rows={18}
                  className="font-mono text-sm"
                  placeholder="Scrivi il contenuto del template..."
                  disabled={editingTemplate?.is_default}
                />
              </div>
            </div>

            {/* Variables sidebar */}
            <div className="space-y-4">
              <div>
                <h4 className="font-medium mb-2">Inserisci Variabile</h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Clicca su una variabile per inserirla nel template
                </p>
              </div>
              <div className="space-y-4 max-h-[400px] overflow-y-auto">
                {Object.entries(availableVariables).map(([category, vars]) => (
                  <div key={category}>
                    <h5 className="text-sm font-medium capitalize mb-2 text-muted-foreground">
                      {category === "broker" ? "Broker" : 
                       category === "client" ? "Cliente" : 
                       category === "policy" ? "Polizza" : "Altro"}
                    </h5>
                    <div className="flex flex-wrap gap-1">
                      {vars.map((v) => (
                        <Button
                          key={v.name}
                          variant="outline"
                          size="sm"
                          className="text-xs h-7"
                          onClick={() => insertVariable(v.name)}
                          disabled={editingTemplate?.is_default}
                          title={v.description}
                        >
                          {`{${v.name}}`}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>
              {editingTemplate?.is_default ? "Chiudi" : "Annulla"}
            </Button>
            {editingTemplate?.is_default ? (
              <Button onClick={() => {
                setEditingTemplate(null);
                setNewTemplateTitle(newTemplateTitle + " (Copia)");
              }}>
                <Copy className="h-4 w-4 mr-2" />
                Crea Copia Personalizzata
              </Button>
            ) : (
              <Button
                onClick={saveTemplate}
                disabled={savingTemplate || !newTemplateTitle || !newTemplateContent}
              >
                {savingTemplate ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Salva Template
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generated Link Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Richiesta Firma Creata!
            </DialogTitle>
            <DialogDescription>
              Documento pronto per {generatedClientName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Link per la firma</Label>
              <div className="flex gap-2 mt-2">
                <Input value={generatedLink} readOnly className="font-mono text-sm" />
                <Button
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(generatedLink);
                    toast.success("Link copiato!");
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <p className="text-sm text-muted-foreground">Invia il link al cliente:</p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    sendViaWhatsApp(generatedSignatureId);
                    setLinkDialogOpen(false);
                  }}
                >
                  <MessageCircle className="h-4 w-4 mr-2 text-green-500" />
                  WhatsApp
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    sendViaEmail(generatedSignatureId);
                    setLinkDialogOpen(false);
                  }}
                >
                  <Mail className="h-4 w-4 mr-2 text-blue-500" />
                  Email
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setLinkDialogOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
